import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

/**
 * PageHeader — A modern, reusable page header component.
 *
 * Props:
 *   title       (string)    — Primary heading text (required)
 *   subtitle    (string)    — Secondary description text
 *   icon        (Component) — Lucide icon component to render in the accent circle
 *   badge       (string)    — Small label rendered as a pill next to the title (e.g. sponsor name)
 *   badgeIcon   (Component) — Optional Lucide icon inside the badge pill
 *   children    (node)      — Right-side slot for action buttons / controls
 *   className   (string)    — Additional classes on the outer wrapper
 */
const PageHeader = ({
  title,
  subtitle,
  icon: Icon,
  badge,
  badgeIcon: BadgeIcon,
  titleAccessory,
  children,
  truncateTitle = false,
  titleMaxWidthClass = '',
  className = '',
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const badgeTextRef = useRef(null);
  const titleTextRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  // Detect whether the badge text is overflowing (truncated)
  useEffect(() => {
    const el = badgeTextRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [badge]);

  useEffect(() => {
    const el = titleTextRef.current;
    if (el) {
      setIsTitleTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [title, truncateTitle]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`mb-10 ${className}`}
    >
      <div className="flex flex-col gap-4">
        {/* Top row: Icon + Text */}
        <div className="flex items-start gap-4 min-w-0">
          {Icon && (
            <div className="flex-shrink-0 bg-primary-50 text-primary-600 rounded-xl w-11 h-11 flex items-center justify-center mt-0.5">
              <Icon className="w-5 h-5" />
            </div>
          )}

          <div className="min-w-0 flex-1">
            {/* Title row with optional badge */}
            <div className="flex items-start gap-3 min-w-0">
              <div
                className={`relative min-w-0 ${truncateTitle ? `max-w-full ${titleMaxWidthClass}`.trim() : ''}`.trim()}
                onMouseEnter={() => truncateTitle && isTitleTruncated && setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <h1
                  ref={titleTextRef}
                  className={`text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight leading-tight break-words ${truncateTitle ? 'truncate max-w-full' : ''}`.trim()}
                >
                  {title}
                </h1>

                {truncateTitle && showTooltip && isTitleTruncated && (
                  <div className="absolute left-0 top-full mt-1.5 z-50 px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-lg dark:shadow-gray-900/40 whitespace-nowrap max-w-[min(90vw,40rem)] overflow-hidden text-ellipsis">
                    {title}
                    <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 rotate-45" />
                  </div>
                )}
              </div>

              {titleAccessory && (
                <div className="flex items-center pt-1 flex-shrink-0">
                  {titleAccessory}
                </div>
              )}

              {badge && (
                <div
                  className="relative flex items-center max-w-[220px] sm:max-w-xs"
                  onMouseEnter={() => isTruncated && setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-100 truncate">
                    {BadgeIcon && <BadgeIcon className="w-3 h-3 flex-shrink-0" />}
                    <span ref={badgeTextRef} className="truncate">
                      {badge}
                    </span>
                  </span>

                  {/* Tooltip for truncated badge */}
                  {showTooltip && (
                    <div className="absolute left-0 top-full mt-1.5 z-50 px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-lg dark:shadow-gray-900/40 whitespace-nowrap">
                      {badge}
                      <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 rotate-45" />
                    </div>
                  )}
                </div>
              )}
            </div>

            {subtitle && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Actions row: below title, wraps naturally */}
        {children && (
          <div className="flex items-center gap-2.5 flex-wrap">
            {children}
          </div>
        )}
      </div>

      {/* Subtle bottom border — gives a Linear-style visual separation */}
      <div className="mt-6 border-b border-gray-200/80 dark:border-gray-700/80" />
    </motion.div>
  );
};

export default PageHeader;
